import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Clock, ShieldAlert, XCircle, Gamepad2, FileText, Headphones, BookOpen, Map, ListChecks, CheckCircle2, XOctagon, Download, MessageCircle, Smile, Calendar, HelpCircle, ArrowRight } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/u9dc2raQn8H9yH6EugsBJP/private_openrouter-generated-image-1776821453880.png';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  text,
  onClick,
  href
}: {
  text: string;
  onClick?: () => void;
  href?: string;
}) => {
  const classes = 'w-full sm:w-auto bg-brand-yellow text-brand-dark font-extrabold text-lg md:text-xl px-8 py-4 rounded-xl shadow-soft flex items-center justify-center gap-3 transition-colors hover:bg-[#E5B508]';
  if (href) {
    const handleClick = () => {
      if (onClick) onClick();
      setTimeout(() => {
        window.open(href, '_blank', 'noopener,noreferrer');
      }, 300);
    };
    return <motion.button whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={handleClick} className={classes} data-id="element-1">
        {text}
        <ArrowRight className="w-6 h-6" data-id="element-2" />
      </motion.button>;
  }
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={classes} data-id="element-3">
      {text}
      <ArrowRight className="w-6 h-6" data-id="element-4" />
    </motion.button>;
};
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
export function App() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  useEffect(() => {
    // Meta Pixel initialization
    if (!window.fbq) {
      const n: any = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
    }
    window.fbq('init', '4511250179106463');
    window.fbq('track', 'PageView');
  }, []);
  const handleAddToCart = () => {
    window.fbq('track', 'AddToCart', {
      content_name: 'Toddler Teeth-Brushing Battle-Plan',
      content_type: 'product',
      value: 6.39,
      currency: 'USD'
    });
  };
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-brand-cream text-brand-dark font-sans overflow-x-hidden" data-id="element-5">
      {/* HERO SECTION */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-32 px-6 max-w-7xl mx-auto" data-id="element-6">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-7">
          <FadeIn data-id="element-8">
            <div className="inline-block bg-brand-blue/10 text-brand-blue font-bold px-4 py-2 rounded-full mb-6" data-id="element-9">
              Para padres de niños de 2 a 5 años
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6 text-brand-dark" data-id="element-10">
              El Plan de Batalla para el Cepillado Dental Infantil:{' '}
              <span className="text-brand-blue" data-id="element-11">
                De la "Guerra en el Baño" a la Paz en 2 Minutos
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-neutral-600 mb-8 font-medium leading-relaxed" data-id="element-12">
              Detén los gritos, las lágrimas y las peleas nocturnas con el
              sistema de "Diseño de Juego" que logra que tu hijo quiera
              cepillarse (sin usar la fuerza).
            </p>
            <CTAButton text="¡QUIERO DETENER LAS PELEAS EN EL BAÑO AHORA!" onClick={scrollToOffer} data-id="element-13" />
          </FadeIn>
          <FadeIn delay={0.2} className="relative" data-id="element-14">
            <div className="absolute inset-0 bg-brand-blue rounded-3xl transform rotate-3 scale-105 opacity-20" data-id="element-15"></div>
            <img src={PRODUCT_IMAGE} alt="The Toddler Teeth-Brushing Battle-Plan" className="relative z-10 rounded-2xl shadow-soft w-full object-cover" data-id="element-16" />
          </FadeIn>
        </div>
      </section>

      {/* OPENING HOOK */}
      <section className="py-20 bg-white px-6" data-id="element-17">
        <div className="max-w-3xl mx-auto text-center" data-id="element-18">
          <FadeIn data-id="element-19">
            <Clock className="w-16 h-16 text-brand-blue mx-auto mb-6" data-id="element-20" />
            <p className="text-2xl md:text-3xl font-bold leading-relaxed mb-6" data-id="element-21">
              Son las 8:30 de la noche. Estás agotado. Solo quieres que se laven
              los dientes y se duerman...
            </p>
            <p className="text-lg md:text-xl text-neutral-600 leading-relaxed" data-id="element-22">
              Pero en cuanto sacas el cepillo de dientes, empieza la pesadilla.
              Tu hijo cierra la boca como una caja fuerte, empieza a gritar y tú
              terminas frustrado, sintiéndote como el "malo de la película"
              mientras intentas mantener su salud dental a salvo.
            </p>
            <p className="text-xl font-bold text-brand-blue mt-8" data-id="element-23">
              No tiene por qué ser así.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 bg-brand-cream px-6" data-id="element-24">
        <div className="max-w-4xl mx-auto" data-id="element-25">
          <FadeIn data-id="element-26">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-card border-t-4 border-brand-blue" data-id="element-27">
              <div className="flex items-center gap-4 mb-6" data-id="element-28">
                <ShieldAlert className="w-10 h-10 text-red-500 flex-shrink-0" data-id="element-29" />
                <h2 className="text-3xl font-bold" data-id="element-30">
                  El verdadero problema no es el cepillo
                </h2>
              </div>
              <p className="text-lg text-neutral-700 mb-6 leading-relaxed" data-id="element-31">
                Obligar a un niño a cepillarse mediante la fuerza física no solo
                es agotador para ti, sino que{' '}
                <strong data-id="element-32">
                  entrena el cerebro de tu hijo para ver la higiene dental como
                  una amenaza
                </strong>
                . Cada vez que "ganas" la pelea forzándolo, pierdes la batalla a
                largo plazo, creando un trauma que hace que la noche siguiente
                sea aún más difícil.
              </p>
              <p className="text-lg text-neutral-700 leading-relaxed" data-id="element-33">
                El problema no es el cepillo, ni el sabor de la pasta, ni
                siquiera la terquedad de tu hijo.{' '}
                <strong data-id="element-34">El problema es la pérdida de autonomía.</strong> Tu hijo
                está en una etapa donde necesita sentir el control, y el baño es
                su campo de batalla favorito para demostrarlo.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 bg-brand-dark text-white px-6" data-id="element-35">
        <div className="max-w-4xl mx-auto" data-id="element-36">
          <FadeIn data-id="element-37">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-38">
              Por qué lo que has intentado no funciona
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-39">
              {['Los sobornos con dulces solo refuerzan el problema que intentas prevenir.', "Las amenazas de 'se te van a caer los dientes' son demasiado abstractas para el cerebro de un niño de 3 años.", "El cepillado forzado activa su respuesta de 'lucha o huida', cerrando su mandíbula por instinto.", 'Comprar 10 cepillos de diferentes colores no soluciona el conflicto de poder subyacente.'].map((text, i) => <div key={i} className="flex gap-4 items-start bg-white/10 p-6 rounded-2xl" data-id="element-40">
                  <XCircle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" data-id="element-41" />
                  <p className="text-lg font-medium" data-id="element-42">{text}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 bg-brand-blue px-6 text-white text-center" data-id="element-43">
        <div className="max-w-4xl mx-auto" data-id="element-44">
          <FadeIn data-id="element-45">
            <Gamepad2 className="w-20 h-20 mx-auto mb-8 text-brand-yellow" data-id="element-46" />
            <h2 className="text-4xl md:text-5xl font-black mb-8" data-id="element-47">
              Presentamos el método "The Tooth Hero"
            </h2>
            <p className="text-xl md:text-2xl font-bold text-brand-yellow mb-6" data-id="element-48">
              (Psicología de Diseño de Juego)
            </p>
            <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" data-id="element-49">
              En lugar de imponer una tarea, este sistema utiliza guiones
              psicológicos y mecánicas de juego para convertir el cepillado en
              una misión de rescate donde el niño es el protagonista. Al
              devolverle la sensación de control a través de "Elecciones
              Guiadas", la resistencia desaparece porque el niño siente que
              cepillarse fue idea suya.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 bg-white px-6" data-id="element-50">
        <div className="max-w-6xl mx-auto" data-id="element-51">
          <FadeIn data-id="element-52">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-16" data-id="element-53">
              Lo que obtienes hoy
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-54">
            {[{
            icon: FileText,
            title: 'La Guía Maestra del Plan de Batalla (PDF)',
            desc: 'El paso a paso para transformar el ambiente en el baño.'
          }, {
            icon: Headphones,
            title: 'Audio de Emergencia para Padres',
            desc: 'Escúchalo en 10 minutos y aprende qué decir exactamente cuando el berrinche comienza.'
          }, {
            icon: BookOpen,
            title: 'El Guion de la "Misión Tooth Hero"',
            desc: 'Una historia narrativa que puedes leer mientras cepillas para mantener su boca abierta por curiosidad.'
          }, {
            icon: Map,
            title: 'El Mapa de Recompensas visual',
            desc: 'Un descargable listo para imprimir que gamifica el progreso semanal.'
          }, {
            icon: ListChecks,
            title: 'Checklist de "Crisis de 60 Segundos"',
            desc: 'Qué hacer cuando nada parece funcionar para resetear el momento.'
          }, {
            icon: Calendar,
            title: 'Plan de 7 Noches "Sin Gritos"',
            desc: 'Una guía día a día con actividades específicas para que en una semana el cepillado sea automático y sin conflictos.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-55">
                <div className="bg-brand-cream p-8 rounded-3xl h-full border border-neutral-200 hover:shadow-soft transition-shadow" data-id="element-56">
                  <div className="bg-brand-blue w-14 h-14 rounded-2xl flex items-center justify-center mb-6" data-id="element-57">
                    <item.icon className="w-7 h-7 text-white" data-id="element-58" />
                  </div>
                  <h3 className="text-xl font-bold mb-3" data-id="element-59">{item.title}</h3>
                  <p className="text-neutral-600" data-id="element-60">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 bg-brand-cream px-6" data-id="element-61">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-62">
          <FadeIn data-id="element-63">
            <div className="bg-white p-8 rounded-3xl shadow-card h-full border-t-8 border-green-400" data-id="element-64">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" data-id="element-65">
                <CheckCircle2 className="w-8 h-8 text-green-500" data-id="element-66" />
                Para quién ES esto:
              </h3>
              <ul className="space-y-6" data-id="element-67">
                {['Padres de niños entre 2 y 5 años que odian el cepillado.', 'Cuidadores que están cansados de las luchas de poder nocturnas.', 'Familias que quieren crear hábitos saludables sin traumas ni gritos.', "Padres que buscan soluciones prácticas y psicológicas, no solo 'consejos médicos'."].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-68">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" data-id="element-69" />
                    <span className="text-neutral-700 font-medium" data-id="element-70">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-71">
            <div className="bg-white p-8 rounded-3xl shadow-card h-full border-t-8 border-red-400" data-id="element-72">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" data-id="element-73">
                <XOctagon className="w-8 h-8 text-red-500" data-id="element-74" />
                Para quién NO es esto:
              </h3>
              <ul className="space-y-6" data-id="element-75">
                {['Padres que prefieren seguir usando la fuerza física como método principal.', 'Personas que buscan una solución mágica que no requiera cambiar la forma en que le hablan a su hijo.', 'Niños con condiciones dentales graves que requieren cirugía inmediata.'].map((text, i) => <li key={i} className="flex gap-4 items-start" data-id="element-76">
                    <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0" data-id="element-77" />
                    <span className="text-neutral-700 font-medium" data-id="element-78">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white px-6" data-id="element-79">
        <div className="max-w-5xl mx-auto" data-id="element-80">
          <FadeIn data-id="element-81">
            <h2 className="text-3xl md:text-4xl font-black text-center mb-16" data-id="element-82">
              Cómo funciona
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-83">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-1 bg-brand-blue/20 -z-10" data-id="element-84"></div>
            {[{
            icon: Download,
            title: '1. Descarga el kit',
            desc: 'Recibe acceso inmediato a la guía y al audio de entrenamiento.'
          }, {
            icon: MessageCircle,
            title: '2. Aplica el Script',
            desc: "Cambia tus preguntas de '¿Quieres?' a la técnica de la 'Doble Alternativa' esta misma noche."
          }, {
            icon: Smile,
            title: '3. Disfruta la Paz',
            desc: "Mira cómo tu hijo pasa de cerrar la boca a abrirla para que el 'Bicho de Azúcar' no gane el juego."
          }].map((step, i) => <FadeIn key={i} delay={i * 0.2} className="text-center" data-id="element-85">
                <div className="w-24 h-24 mx-auto bg-brand-blue rounded-full flex items-center justify-center mb-6 shadow-soft border-4 border-white" data-id="element-86">
                  <step.icon className="w-10 h-10 text-white" data-id="element-87" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-88">{step.title}</h3>
                <p className="text-neutral-600 text-lg" data-id="element-89">{step.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-20 bg-brand-blue text-white px-6" data-id="element-90">
        <div className="max-w-4xl mx-auto" data-id="element-91">
          <FadeIn data-id="element-92">
            <div className="text-center mb-12" data-id="element-93">
              <Calendar className="w-16 h-16 mx-auto mb-6 text-brand-yellow" data-id="element-94" />
              <h2 className="text-3xl md:text-4xl font-black" data-id="element-95">
                ¿Qué cambia en 7 días?
              </h2>
            </div>
            <div className="space-y-6" data-id="element-96">
              {[{
              day: 'La primera noche',
              desc: 'Usarás el script de elección y notarás menos tensión.'
            }, {
              day: 'Para la tercera noche',
              desc: 'El mapa de recompensas hará que el niño te pida ir al baño.'
            }, {
              day: 'Para la séptima noche',
              desc: 'El cepillado será una rutina de 2 minutos sin un solo grito, permitiéndote terminar el día con un beso de buenas noches en lugar de una discusión.'
            }].map((item, i) => <div key={i} className="bg-white/10 p-6 md:p-8 rounded-2xl flex flex-col md:flex-row gap-4 md:gap-8 items-start md:items-center" data-id="element-97">
                  <div className="bg-brand-yellow text-brand-dark font-black px-6 py-2 rounded-full whitespace-nowrap" data-id="element-98">
                    {item.day}
                  </div>
                  <p className="text-lg md:text-xl font-medium" data-id="element-99">{item.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-brand-cream px-6" data-id="element-100">
        <div className="max-w-3xl mx-auto" data-id="element-101">
          <FadeIn data-id="element-102">
            <div className="text-center mb-16" data-id="element-103">
              <HelpCircle className="w-16 h-16 mx-auto mb-6 text-brand-blue" data-id="element-104" />
              <h2 className="text-3xl md:text-4xl font-black" data-id="element-105">
                Preguntas Frecuentes
              </h2>
            </div>
            <div className="space-y-6" data-id="element-106">
              {[{
              q: '¿Funciona con niños que tienen sensibilidad sensorial?',
              a: 'Sí, incluimos una sección específica sobre ajustes de luz y temperatura para niños sensibles.'
            }, {
              q: '¿Tengo que comprar cepillos caros?',
              a: 'No, el método funciona con cualquier cepillo que tengas en casa.'
            }, {
              q: '¿Cuánto tiempo toma ver resultados?',
              a: 'La mayoría de los padres reportan una reducción del 50% en la resistencia desde la primera noche.'
            }, {
              q: '¿El audio es para el niño o para mí?',
              a: 'Es para ti, el padre, para que aprendas la técnica mientras haces otras cosas.'
            }].map((faq, i) => <div key={i} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm" data-id="element-107">
                  <h4 className="text-xl font-bold mb-3 text-brand-dark" data-id="element-108">
                    {faq.q}
                  </h4>
                  <p className="text-neutral-600 text-lg" data-id="element-109">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section id="offer-section" className="py-24 bg-white px-6 border-t border-neutral-100" data-id="element-110">
        <div className="max-w-5xl mx-auto" data-id="element-111">
          <FadeIn data-id="element-112">
            <div className="bg-brand-blue rounded-3xl p-8 md:p-12 lg:p-16 text-white shadow-soft relative overflow-hidden" data-id="element-113">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-114"></div>

              <div className="grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-115">
                <div data-id="element-116">
                  <h2 className="text-3xl md:text-4xl font-black mb-6 leading-tight" data-id="element-117">
                    Recupera la paz de tus noches y protege la sonrisa de tu
                    hijo hoy mismo.
                  </h2>
                  <p className="text-lg md:text-xl text-blue-100 mb-8" data-id="element-118">
                    Olvida las facturas dentales por caries evitables y el
                    estrés de cada noche. Consigue el Toddler Teeth-Brushing
                    Battle-Plan completo por solo:
                  </p>
                  <div className="mb-8" data-id="element-119">
                    <div className="flex items-center gap-4 flex-wrap" data-id="element-120">
                      <span className="text-3xl font-bold text-white/50 line-through" data-id="element-121">
                        $24
                      </span>
                      <span className="text-6xl font-black text-brand-yellow" data-id="element-122">
                        $6.39
                      </span>
                      <span className="bg-red-500 text-white text-sm font-extrabold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-123">
                        -73% OFF
                      </span>
                    </div>
                  </div>
                  <CTAButton text="¡QUIERO DETENER LAS PELEAS AHORA!" href="https://www.earlysmileco.com/cart/53665283375470:1" onClick={handleAddToCart} data-id="element-124" />
                </div>

                <div className="relative" data-id="element-125">
                  <img src={PRODUCT_IMAGE} alt="The Toddler Teeth-Brushing Battle-Plan" className="rounded-2xl shadow-2xl w-full max-w-md mx-auto transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-126" />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* LEGAL MODALS */}
      {showPrivacy && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setShowPrivacy(false)} data-id="element-127">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12 relative" onClick={e => e.stopPropagation()} data-id="element-128">
            <button onClick={() => setShowPrivacy(false)} className="absolute top-4 right-4 text-neutral-400 hover:text-brand-dark" data-id="element-129">
              <XCircle className="w-7 h-7" data-id="element-130" />
            </button>
            <h2 className="text-3xl font-black mb-6 text-brand-dark" data-id="element-131">
              Política de Privacidad
            </h2>
            <div className="space-y-4 text-neutral-700 leading-relaxed" data-id="element-132">
              <p data-id="element-133">
                <strong data-id="element-134">Última actualización:</strong> Abril 2026
              </p>
              <p data-id="element-135">
                En The Toddler Teeth-Brushing Battle-Plan, respetamos tu
                privacidad y nos comprometemos a proteger tu información
                personal.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-136">
                Información que recopilamos
              </h3>
              <p data-id="element-137">
                Recopilamos únicamente la información necesaria para procesar tu
                compra: nombre, dirección de correo electrónico e información de
                pago. No almacenamos datos de tarjetas de crédito en nuestros
                servidores.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-138">
                Uso de la información
              </h3>
              <p data-id="element-139">
                Tu información se utiliza exclusivamente para: entregar el
                producto digital adquirido, enviar actualizaciones relacionadas
                con tu compra y mejorar nuestros servicios.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-140">
                Compartir información
              </h3>
              <p data-id="element-141">
                No vendemos, intercambiamos ni transferimos tu información
                personal a terceros, excepto proveedores de servicios de pago
                necesarios para completar la transacción.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-142">
                Cookies
              </h3>
              <p data-id="element-143">
                Utilizamos cookies esenciales para el funcionamiento del sitio y
                cookies analíticas para entender cómo los visitantes interactúan
                con nuestra página.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-144">
                Tus derechos
              </h3>
              <p data-id="element-145">
                Puedes solicitar acceso, corrección o eliminación de tus datos
                personales en cualquier momento.
              </p>
            </div>
          </motion.div>
        </div>}

      {showTerms && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setShowTerms(false)} data-id="element-146">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12 relative" onClick={e => e.stopPropagation()} data-id="element-147">
            <button onClick={() => setShowTerms(false)} className="absolute top-4 right-4 text-neutral-400 hover:text-brand-dark" data-id="element-148">
              <XCircle className="w-7 h-7" data-id="element-149" />
            </button>
            <h2 className="text-3xl font-black mb-6 text-brand-dark" data-id="element-150">
              Términos y Condiciones
            </h2>
            <div className="space-y-4 text-neutral-700 leading-relaxed" data-id="element-151">
              <p data-id="element-152">
                <strong data-id="element-153">Última actualización:</strong> Abril 2026
              </p>
              <p data-id="element-154">
                Al adquirir The Toddler Teeth-Brushing Battle-Plan, aceptas los
                siguientes términos y condiciones.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-155">
                Producto digital
              </h3>
              <p data-id="element-156">
                Este es un producto digital descargable. Una vez completada la
                compra, recibirás acceso inmediato a todos los materiales
                incluidos en el kit: guía PDF, audio, guion narrativo, mapa de
                recompensas y checklist.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-157">
                Uso personal
              </h3>
              <p data-id="element-158">
                El contenido es para uso personal y familiar únicamente. Queda
                prohibida la reproducción, distribución o reventa del material
                sin autorización expresa.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-159">
                Resultados
              </h3>
              <p data-id="element-160">
                Los resultados pueden variar según cada niño y familia. Las
                experiencias compartidas son testimonios reales, pero no
                garantizamos resultados idénticos para todos los usuarios.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-161">
                Reembolsos
              </h3>
              <p data-id="element-162">
                Ofrecemos una garantía de satisfacción de 30 días. Si no estás
                satisfecho con el producto, puedes solicitar un reembolso
                completo dentro de los primeros 30 días posteriores a la compra.
              </p>
              <h3 className="text-xl font-bold text-brand-dark pt-2" data-id="element-163">
                Aviso médico
              </h3>
              <p data-id="element-164">
                Este producto no sustituye el consejo médico profesional. Si tu
                hijo tiene condiciones dentales que requieren atención
                especializada, consulta con un odontopediatra.
              </p>
            </div>
          </motion.div>
        </div>}

      {/* FOOTER */}
      <footer className="py-8 bg-brand-dark text-neutral-400 px-6" data-id="element-165">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-166">
          <p className="text-sm" data-id="element-167">
            © {new Date().getFullYear()} The Toddler Teeth-Brushing
            Battle-Plan. Todos los derechos reservados.
          </p>
          <div className="flex gap-6" data-id="element-168">
            <button onClick={() => setShowPrivacy(true)} className="text-sm hover:text-white transition-colors underline underline-offset-2" data-id="element-169">
              Política de Privacidad
            </button>
            <button onClick={() => setShowTerms(true)} className="text-sm hover:text-white transition-colors underline underline-offset-2" data-id="element-170">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>
    </div>;
}